/**
 * StepStepper — header visual del wizard.
 *
 * Renderiza los 5 pasos con indicador del actual + check para completados.
 * Click en un paso anterior permite saltar atrás (forward navigation está
 * controlada por validación del orchestrator).
 */

import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import type { WizardState } from './types'

interface StepStepperProps {
  current: WizardState['step']
  onJumpTo: (step: WizardState['step']) => void
}

const STEPS: Array<{ n: WizardState['step']; key: string }> = [
  { n: 1, key: 'step_1' },
  { n: 2, key: 'step_2' },
  { n: 3, key: 'step_3' },
  { n: 4, key: 'step_4' },
  { n: 5, key: 'step_5' },
]

export function StepStepper({ current, onJumpTo }: StepStepperProps) {
  const { t } = useTranslation('projects')

  return (
    <ol className="flex items-center justify-between gap-2 text-xs">
      {STEPS.map((step, i) => {
        const isActive = step.n === current
        const isComplete = step.n < current
        const canJump = step.n < current
        return (
          <Fragment key={step.n}>
            <li className="flex items-center gap-2">
              <button
                type="button"
                disabled={!canJump}
                onClick={() => canJump && onJumpTo(step.n)}
                className={
                  isActive
                    ? 'flex items-center gap-2 cursor-default'
                    : canJump
                      ? 'flex items-center gap-2 cursor-pointer hover:text-ink'
                      : 'flex items-center gap-2 cursor-not-allowed'
                }
              >
                <span
                  className={
                    isActive
                      ? 'w-5 h-5 rounded-full bg-brand text-canvas grid place-items-center text-[10px] font-semibold'
                      : isComplete
                        ? 'w-5 h-5 rounded-full bg-official text-canvas grid place-items-center text-[10px] font-semibold'
                        : 'w-5 h-5 rounded-full border border-line-strong grid place-items-center text-[10px] text-ink-muted'
                  }
                  aria-current={isActive ? 'step' : undefined}
                >
                  {step.n}
                </span>
                <span
                  className={
                    isActive
                      ? 'font-medium text-ink whitespace-nowrap'
                      : 'text-ink-muted whitespace-nowrap'
                  }
                >
                  {t(`wizard.stepper.${step.key}` as const)}
                </span>
              </button>
            </li>
            {i < STEPS.length - 1 ? (
              <li aria-hidden="true" className="flex-1 h-px bg-line" />
            ) : null}
          </Fragment>
        )
      })}
    </ol>
  )
}
